/**
 * SettingsScreen — pixel-faithful port of the Settings surface in
 * `design/project/screens-misc.jsx`.
 *
 * Layout (top → bottom):
 *   1. Title 「設定」
 *   2. User-card hero: avatar + display name + email + 已同步 chip
 *   3. Group 「資料」 — 帳本管理 / 分類管理 / 主要顯示幣別 / 固定週期
 *   4. Group 「安全」 — 雲端同步
 *   5. Group 「其他」 — 通知 / 關於 Mochia v1.0.0
 *   6. Destructive 「登出」 button (with confirm modal)
 *
 * 主要顯示幣別:
 *   - Reads `meta.primary_currency` from the parent (passed in via the
 *     `primaryCurrency` prop, defaulting to 'TWD' when unavailable).
 *   - Tap opens a small picker bottom sheet with TWD / JPY / USD / EUR.
 *   - On pick: optimistic update via `onPrimaryCurrencyChange` + caller
 *     enqueues `set_meta` (parent owns the sync queue).
 *
 * Sign-out flow (per task 15.2 / 2.7) — see prior commit history. Briefly:
 *   1. postLogout() fire-and-forget (server revokes Google grant + clears cookie)
 *   2. clearCookieKnown() (so next cold load skips /api/auth/refresh)
 *   3. clearSession() (local in-memory wipe)
 *   4. reset every data slice
 *   5. navigate('/login')
 *
 * Sharp edges:
 *   - Sub-screens are still routed via `onNavigate` (parent owns router).
 *   - 主要顯示幣別 picker is rendered inline (no portal); it sits above the
 *     surface but inside the same overflow container, so the iOS frame
 *     test fixtures continue to clip correctly.
 */
import { useState } from 'react';
import Icon, { type IconName } from '@/components/common/Icon';
import Modal from '@/components/common/Modal';
import BottomSheet from '@/components/common/BottomSheet';
import { useStore } from '@/state/store';
import { postLogout } from '@/lib/auth/serverRefresh';
import { clearCookieKnown } from '@/lib/auth/cookieKnown';
import { navigate as routerNavigate, type RoutePath } from '@/lib/router';
import { buildJoinUrl } from '@/lib/sharing';
import { getActiveSpreadsheetId } from '@/lib/sheets/activeSpreadsheet';
import { hapticsSupported } from '@/lib/haptics';

type CurrencyCode = 'TWD' | 'JPY' | 'USD' | 'EUR';

const CURRENCY_OPTIONS: ReadonlyArray<{ code: CurrencyCode; name: string }> = [
  { code: 'TWD', name: '新台幣' },
  { code: 'JPY', name: '日圓' },
  { code: 'USD', name: '美元' },
  { code: 'EUR', name: '歐元' },
];

interface SettingsRow {
  key: string;
  icon: IconName;
  iconColor: string;
  title: string;
  detail?: string;
  to?: RoutePath;
  onClick?: () => void;
  /** Informational, non-interactive row: no chevron, no tap affordance.
   * Used for status-only rows (e.g. haptic support on the current device). */
  info?: boolean;
}

export interface SettingsScreenProps {
  onNavigate: (to: RoutePath) => void;
  /** Optional details to display per row (e.g. "3 本"). */
  details?: Partial<Record<string, string>>;
  /** Current primary currency. Defaults to 'TWD' when not provided. */
  primaryCurrency?: string;
  /** Optimistic-update callback when the user picks a new primary currency. */
  onPrimaryCurrencyChange?: (code: string) => void;
}

export default function SettingsScreen({
  onNavigate,
  details,
  primaryCurrency = 'TWD',
  onPrimaryCurrencyChange,
}: SettingsScreenProps): JSX.Element {
  const email = useStore((s) => s.auth.email);
  const name = useStore((s) => s.auth.name);
  const picture = useStore((s) => s.auth.picture);
  const [confirming, setConfirming] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [ccyOpen, setCcyOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const performSignOut = async (): Promise<void> => {
    if (signingOut) return;
    setSigningOut(true);
    // Fire-and-forget: server revokes the Google grant and clears the HttpOnly
    // cookie. postLogout never throws — local cleanup always proceeds.
    void postLogout();
    clearCookieKnown();
    const state = useStore.getState();
    state.auth.clearSession();
    state.books.setItems([]);
    state.categories.setItems([]);
    state.transactions.setItems([]);
    state.recurring.setItems([]);
    state.notifications.setItems([]);
    state.sync.reset();
    routerNavigate('/login');
    setConfirming(false);
    setSigningOut(false);
  };

  const ccyDetail = (() => {
    const match = CURRENCY_OPTIONS.find((c) => c.code === primaryCurrency);
    return match
      ? `${match.code} · ${match.name}`
      : primaryCurrency;
  })();

  const dataRows: SettingsRow[] = [
    {
      key: 'books',
      icon: 'book',
      iconColor: 'var(--matcha-500)',
      title: '帳本管理',
      detail: details?.books,
      to: '/settings/books',
    },
    {
      key: 'cats',
      icon: 'tag',
      iconColor: 'var(--honey-500)',
      title: '分類管理',
      detail: details?.cats,
      to: '/settings/cats',
    },
    {
      key: 'ccy',
      icon: 'coins',
      iconColor: 'var(--honey-500)',
      title: '主要顯示幣別',
      detail: ccyDetail,
      onClick: () => setCcyOpen(true),
    },
    {
      key: 'recur',
      icon: 'repeat',
      iconColor: 'var(--cat-home)',
      title: '固定週期',
      detail: details?.recur,
      to: '/recur',
    },
    {
      key: 'share',
      icon: 'user',
      iconColor: 'var(--cat-fun)',
      title: '共享給家人',
      detail: '產生分享連結',
      onClick: () => setShareOpen(true),
    },
  ];

  const securityRows: SettingsRow[] = [
    {
      key: 'sync',
      icon: 'cloud',
      iconColor: 'var(--cat-transit)',
      title: '雲端同步',
      detail: details?.sync,
      to: '/settings/sync',
    },
  ];

  // Haptics is delivered via the Vibration API, which iOS Safari does not
  // implement. Rather than offer a toggle that does nothing on iPhone, we show
  // an honest status row: "已啟用" where the API exists, "此裝置不支援" where
  // it doesn't. Computed at render — capability is stable within a session.
  const hapticsOk = hapticsSupported();
  const otherRows: SettingsRow[] = [
    {
      key: 'notif',
      icon: 'bell',
      iconColor: 'var(--cat-fun)',
      title: '通知設定',
      detail: details?.notif,
      to: '/settings/notif',
    },
    {
      key: 'haptics',
      icon: 'display',
      iconColor: 'var(--cat-transit)',
      title: '觸控回饋',
      detail: hapticsOk ? '已啟用' : '此裝置不支援',
      info: true,
    },
    {
      key: 'about',
      icon: 'note',
      iconColor: 'var(--cat-edu)',
      title: '關於 Mochia',
      detail: details?.about ?? 'v1.0.0',
    },
  ];

  const renderRow = (r: SettingsRow, i: number, total: number): JSX.Element => (
    <li key={r.key}>
      <button
        type="button"
        disabled={r.info}
        onClick={() => {
          if (r.info) return;
          if (r.onClick) {
            r.onClick();
          } else if (r.to) {
            onNavigate(r.to);
          }
        }}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '12px 14px',
          width: '100%',
          textAlign: 'left',
          background: 'transparent',
          border: 0,
          cursor: r.info ? 'default' : 'pointer',
          borderBottom: i < total - 1 ? '0.5px solid var(--border-1)' : '0',
        }}
      >
        <div
          style={{
            width: 30,
            height: 30,
            borderRadius: 8,
            background: r.iconColor,
            display: 'grid',
            placeItems: 'center',
            flexShrink: 0,
          }}
        >
          <Icon name={r.icon} size={16} stroke="#fff" />
        </div>
        <div
          style={{
            flex: 1,
            font: '500 15px var(--font-cjk)',
            color: 'var(--fg-1)',
          }}
        >
          {r.title}
        </div>
        {r.detail && (
          <span
            style={{
              font: '400 13px var(--font-cjk)',
              color: 'var(--fg-3)',
            }}
          >
            {r.detail}
          </span>
        )}
        {!r.info && <Icon name="chevR" size={14} stroke="var(--fg-4)" />}
      </button>
    </li>
  );

  const Group = ({
    title,
    rows,
  }: {
    title: string;
    rows: SettingsRow[];
  }): JSX.Element => (
    <div style={{ marginBottom: 22 }}>
      <div
        style={{
          font: '500 11px var(--font-cjk)',
          color: 'var(--fg-3)',
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          padding: '0 20px 6px',
        }}
      >
        {title}
      </div>
      <ul
        style={{
          margin: '0 16px',
          background: 'var(--bg-surface)',
          borderRadius: 16,
          boxShadow: 'var(--shadow-1)',
          overflow: 'hidden',
          listStyle: 'none',
          padding: 0,
        }}
        aria-label={title}
      >
        {rows.map((r, i) => renderRow(r, i, rows.length))}
      </ul>
    </div>
  );

  // Display name priority: Google profile name → email local-part →
  // 「未登入」. Avatar priority: profile picture URL → first letter of
  // display name (terracotta circle) → 「U」.
  const displayName: string =
    name || (email ? email.split('@')[0] ?? '' : '') || '未登入';
  const initial = displayName.trim().charAt(0).toUpperCase() || 'U';

  return (
    <div
      style={{
        height: '100%',
        overflow: 'auto',
        paddingBottom: 120,
        background: 'var(--bg-app)',
      }}
    >
      <div style={{ padding: '60px 20px 16px' }}>
        <h1
          style={{
            font: '600 26px var(--font-cjk-display)',
            color: 'var(--fg-1)',
            letterSpacing: '-0.01em',
            margin: 0,
          }}
        >
          設定
        </h1>
      </div>

      {/* User card hero */}
      <div
        data-testid="account-row"
        style={{
          margin: '4px 16px 22px',
          background: 'var(--bg-surface)',
          borderRadius: 16,
          padding: 16,
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          boxShadow: 'var(--shadow-1)',
        }}
      >
        {picture ? (
          <img
            src={picture}
            alt={displayName}
            referrerPolicy="no-referrer"
            // Google CDN photos sometimes 4xx after token rotation; the
            // onError swap drops the broken request and re-renders with
            // the letter fallback so the card never shows a broken-img
            // glyph.
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = 'none';
            }}
            style={{
              width: 52,
              height: 52,
              borderRadius: 26,
              objectFit: 'cover',
              flexShrink: 0,
            }}
          />
        ) : (
          <div
            style={{
              width: 52,
              height: 52,
              borderRadius: 26,
              background: 'var(--terracotta-500)',
              display: 'grid',
              placeItems: 'center',
              color: '#fff',
              font: '600 20px var(--font-cjk-display)',
              flexShrink: 0,
            }}
          >
            {initial}
          </div>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              font: '600 16px var(--font-cjk)',
              color: 'var(--fg-1)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {displayName}
          </div>
          <div
            style={{
              font: '400 12px var(--font-cjk)',
              color: 'var(--fg-3)',
              marginTop: 2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {email ?? '尚未登入'}
          </div>
        </div>
        <span
          style={{
            font: '500 10px var(--font-cjk)',
            color: 'var(--matcha-700)',
            background: 'rgba(126,139,90,0.14)',
            padding: '3px 8px',
            borderRadius: 999,
            flexShrink: 0,
          }}
        >
          已同步
        </span>
      </div>

      <Group title="資料" rows={dataRows} />
      <Group title="安全" rows={securityRows} />
      <Group title="其他" rows={otherRows} />

      <div style={{ padding: '4px 20px 40px' }}>
        <button
          type="button"
          onClick={() => setConfirming(true)}
          style={{
            width: '100%',
            padding: 14,
            background: 'var(--bg-surface)',
            color: 'var(--danger)',
            border: '0.5px solid var(--border-1)',
            borderRadius: 14,
            font: '500 15px var(--font-cjk)',
            cursor: 'pointer',
          }}
        >
          登出
        </button>
      </div>

      {/* Currency picker */}
      <BottomSheet
        open={ccyOpen}
        onClose={() => setCcyOpen(false)}
        title="主要顯示幣別"
      >
        <div
          style={{
            font: '400 12px/1.55 var(--font-cjk)',
            color: 'var(--fg-3)',
            paddingBottom: 12,
          }}
        >
          所有頁面會以此幣別顯示換算後金額。外幣交易同時顯示原幣。
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {CURRENCY_OPTIONS.map((c) => {
            const sel = c.code === primaryCurrency;
            return (
              <button
                key={c.code}
                type="button"
                onClick={() => {
                  onPrimaryCurrencyChange?.(c.code);
                  setCcyOpen(false);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '12px 8px',
                  background: 'transparent',
                  border: 0,
                  borderRadius: 10,
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                <span
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 10,
                    background: 'var(--bg-raised)',
                    display: 'grid',
                    placeItems: 'center',
                    font: '600 13px var(--font-num)',
                    color: 'var(--fg-1)',
                    flexShrink: 0,
                  }}
                  aria-hidden="true"
                >
                  {c.code}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      font: '500 14px var(--font-cjk)',
                      color: 'var(--fg-1)',
                    }}
                  >
                    {c.name}
                  </div>
                  <div
                    style={{
                      font: '400 11.5px var(--font-cjk)',
                      color: 'var(--fg-3)',
                      marginTop: 2,
                    }}
                  >
                    {c.code}
                  </div>
                </div>
                {sel && <Icon name="check" size={18} stroke="var(--accent)" />}
              </button>
            );
          })}
        </div>
      </BottomSheet>

      {/* Share-with-family bottom sheet */}
      <BottomSheet
        open={shareOpen}
        onClose={() => {
          setShareOpen(false);
          setCopied(false);
        }}
        title="共享給家人"
      >
        <ShareSheetBody copied={copied} setCopied={setCopied} />
      </BottomSheet>

      <Modal
        open={confirming}
        onClose={() => !signingOut && setConfirming(false)}
        title="確定登出？"
        width={360}
      >
        <div
          style={{
            font: '400 14px/1.6 var(--font-cjk)',
            color: 'var(--fg-2)',
            marginBottom: 18,
          }}
        >
          登出後將清除本機的暫存資料。下次登入時會重新從 Google 試算表載入。
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={() => setConfirming(false)}
            disabled={signingOut}
            style={{
              padding: '10px 18px',
              background: 'transparent',
              border: '1px solid var(--border-1)',
              borderRadius: 10,
              color: 'var(--fg-2)',
              font: '500 14px var(--font-cjk)',
              cursor: signingOut ? 'not-allowed' : 'pointer',
            }}
          >
            取消
          </button>
          <button
            type="button"
            onClick={() => {
              void performSignOut();
            }}
            disabled={signingOut}
            style={{
              padding: '10px 18px',
              background: 'var(--danger)',
              border: 0,
              borderRadius: 10,
              color: '#fff',
              font: '600 14px var(--font-cjk)',
              cursor: signingOut ? 'not-allowed' : 'pointer',
              opacity: signingOut ? 0.7 : 1,
            }}
          >
            {signingOut ? '登出中…' : '確定登出'}
          </button>
        </div>
      </Modal>
    </div>
  );
}

interface ShareSheetBodyProps {
  copied: boolean;
  setCopied: (v: boolean) => void;
}

function ShareSheetBody({ copied, setCopied }: ShareSheetBodyProps): JSX.Element {
  const sheetId = getActiveSpreadsheetId();
  const driveUrl = sheetId
    ? `https://docs.google.com/spreadsheets/d/${sheetId}/edit`
    : null;
  const joinUrl = sheetId ? buildJoinUrl(sheetId) : '';

  const onCopy = async (): Promise<void> => {
    if (!joinUrl) return;
    try {
      await navigator.clipboard.writeText(joinUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard blocked — user can long-press the URL field instead */
    }
  };

  if (!sheetId) {
    return (
      <div style={{ font: '400 13px/1.6 var(--font-cjk)', color: 'var(--fg-3)' }}>
        試算表尚未載入完成，請稍後再試。
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ font: '400 13px/1.6 var(--font-cjk)', color: 'var(--fg-2)' }}>
        把這份家庭帳本分享給另一半 / 家人，兩人記的帳會即時同步。共需兩個步驟：
      </div>

      <div style={{ font: '500 13px var(--font-cjk)', color: 'var(--fg-1)' }}>
        ① 在 Google Drive 把試算表設成可編輯
      </div>
      <div style={{ font: '400 12px/1.6 var(--font-cjk)', color: 'var(--fg-3)' }}>
        打開下方試算表 → 右上「共用」→ 加入對方 Google 帳號 → 權限選「編輯者」。
      </div>
      {driveUrl && (
        <a
          href={driveUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '8px 14px',
            background: 'var(--bg-raised)',
            borderRadius: 999,
            font: '500 13px var(--font-cjk)',
            color: 'var(--accent)',
            border: '1px solid var(--border-1)',
            alignSelf: 'flex-start',
            textDecoration: 'none',
          }}
        >
          開啟 Google Drive 共用設定
          <Icon name="chevR" size={12} stroke="var(--accent)" />
        </a>
      )}

      <div
        style={{
          font: '500 13px var(--font-cjk)',
          color: 'var(--fg-1)',
          marginTop: 6,
        }}
      >
        ② 把這個連結傳給對方
      </div>
      <div style={{ font: '400 12px/1.6 var(--font-cjk)', color: 'var(--fg-3)' }}>
        對方打開連結 → Google 登入 → 自動進到家庭帳本。
      </div>
      <div
        style={{
          padding: '10px 12px',
          background: 'var(--bg-raised)',
          borderRadius: 10,
          font: '400 11px/1.5 var(--font-num)',
          color: 'var(--fg-1)',
          wordBreak: 'break-all',
          border: '0.5px solid var(--border-1)',
        }}
      >
        {joinUrl}
      </div>
      <button
        type="button"
        onClick={() => void onCopy()}
        style={{
          padding: '12px',
          background: copied ? 'var(--income)' : 'var(--accent)',
          color: '#fff',
          border: 0,
          borderRadius: 12,
          font: '600 14px var(--font-cjk)',
          cursor: 'pointer',
          transition: 'background 200ms',
        }}
      >
        {copied ? '✓ 已複製' : '複製分享連結'}
      </button>
    </div>
  );
}
